import { useLayoutEffect, useReducer, useEffect, useMemo, useState, useRef } from "react";

import dayjs from "dayjs";
import isEqual from "lodash/isEqual";

import Sentry from "@similarweb/sentry-logger";
import { BasicModal } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { capitalizeFirstLetterFilter } from "app/filters/ngFilters";
import { useConfigurationOptions } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/hooks/useConfigurationOptions";
import { useDetectedBrands } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/hooks/useDetectedBrands";
import { useDiscardChangesModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/hooks/useDiscardChangesModal";
import { useGeneratedCompetitors } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/hooks/useGeneratedCompetitors";
import { useSetupPrompts } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/hooks/useSetupPrompts";
import {
    useInitialTopics,
    useUpdateTopics,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/hooks/useTopics";
import type {
    IStepData,
    IStepError,
    IStepHandlers,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/types";
import { usePromptsEditModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/usePromptsEditModal";
import type {
    IAiTrackerCreateCampaignResponse,
    ICreateCampaignBasePayload,
    IUpdateCampaignCompetitor,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/llm-tracker-service/llmTrackerCampignsQueries";
import { campaignKeys } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/llm-tracker-service/llmTrackerCampignsQueries";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import type { ICountryObject } from "app/services/CountryService";
import CountryService from "app/services/CountryService";
import { SwRedux } from "app/services/SwRedux";
import { allTrackers } from "app/services/track/track";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import queryClient from "single-spa/react-app/query-client";

import type { IModifiableTopic } from "../reports/prompts-report/edit-modal/types";
import {
    useLLMTrackerCampaignById,
    useLLMTrackerCampaigns,
    useCreateCampaign,
    useCreateCampaignWithPrompts,
} from "../shared/llm-tracker-service/useLLMTrackerCampaigns";
import type { ExplicitLLModel, IAiTrackerCampaign, ILanguage, IRawCompetitor } from "../shared/types";

import { LLMSetupFooter } from "./components/LLMSetupFooter";
import { LLMSetupForm } from "./components/LLMSetupForm";
import { LLMSetupHeader } from "./components/LLMSetupHeader";
import { FormStepProvider } from "./context/FormStepContext";
import type { ILocaleState } from "./LLMSetupConst";
import { STEPS_COUNT, reducer, initialState, DEFAULT_LANGUAGE, LLM_SETUP_MODAL_HEIGHT } from "./LLMSetupConst";
import { STEP_CONFIGS } from "./stepConfig";
import { composeCampaignName, getUniqueDomainUrls } from "./utils";

interface ILLMSetupModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onSuccessCallback?: (domain: IWebsiteItem | null) => void;
    onCampaignCreatedNavigation: (campaign: IAiTrackerCampaign) => void;
    domain: IWebsiteItem;
}

export const LLMSetupModal = (props: ILLMSetupModalProps) => {
    const { isOpen, setIsOpen, domain, onSuccessCallback, onCampaignCreatedNavigation } = props;
    const [state, dispatch] = useReducer(reducer, initialState);
    const {
        currentStep,
        selectedLanguage,
        brandNames,
        detectedBrandNames,
        domains,
        suggestedTopics,
        selectedTopics,
        promptsPerTopic,
        competitors,
        selectedCountry,
        selectedModels,
        includeSubdomains,
        shouldGenerateCompetitors,
    } = state;

    const swRedux = SwRedux.getInstance();
    const userId = swSettings.user.id as number;
    const prevLocaleStateRef = useRef<ILocaleState>(undefined);
    const [createdCampaignId, setCreatedCampaignId] = useState<string>();
    const [showStepErrors, setShowStepErrors] = useState<boolean>(false);
    const [promptsData, setPromptsData] = useState<IModifiableTopic[]>([]);
    const campaignDomainUrls = useMemo<string[]>(() => getUniqueDomainUrls(domains), [domains]);
    const primaryDomain = campaignDomainUrls?.[0] ?? "";
    const { data: allCampaigns } = useLLMTrackerCampaigns();
    const existingCampaignNames = useMemo<string[]>(() => {
        const userIdStr = String(userId);
        return (
            allCampaigns
                ?.filter((campaign) => campaign.assetPermissions?.ownerUserId === userIdStr)
                .map((campaign) => campaign.name) ?? []
        );
    }, [allCampaigns, userId]);

    const { data: createdCampaign, refetch: refetchCampaigns } = useLLMTrackerCampaignById(createdCampaignId);
    const { mutate: createCampaign, isPending: isCreatingCampaign } = useCreateCampaign();
    const { mutate: createCampaignWithPrompts, isPending: isCreatingCampaignWithPrompts } =
        useCreateCampaignWithPrompts();
    const { data: trackerConfigurationsData, isLoading: areTrackerConfigurationsLoading } = useConfigurationOptions();
    const { data: { country: initialCountry, topics: initialSuggestedTopics } = {}, isFetching: isInitialDataLoading } =
        useInitialTopics(primaryDomain, userId, {
            enabled: isOpen && !!campaignDomainUrls?.length,
        });

    const [setupDomain = ""] = useMemo<string[]>(() => getUniqueDomainUrls(domain ? [domain] : []), [domain]);
    const { data: fetchedDetectedBrands } = useDetectedBrands(setupDomain, {
        enabled: isOpen && !!setupDomain,
    });

    const campaignName = useMemo<string>(
        () => composeCampaignName(brandNames[0], selectedCountry.code, existingCampaignNames),
        [brandNames, selectedCountry, existingCampaignNames],
    );

    const currentStepConfig = STEP_CONFIGS?.[currentStep];
    const isLastStep = currentStep === STEPS_COUNT;
    const canGoBack = currentStep > 1;
    const isDetectingBrands = detectedBrandNames === null;

    const { data: updatedTopicsData, isLoading: areDynamicTopicsLoading } = useUpdateTopics(
        primaryDomain,
        userId,
        selectedCountry.id,
        selectedLanguage,
        {
            enabled:
                isOpen &&
                initialSuggestedTopics !== undefined &&
                (selectedCountry.id !== prevLocaleStateRef.current?.countryId ||
                    selectedLanguage.id !== prevLocaleStateRef.current?.langId),
        },
    );

    const hasChanges = useMemo<boolean>(
        () =>
            !isEqual(brandNames, detectedBrandNames ?? []) ||
            !!selectedTopics.length ||
            competitors.some((c) => !!c.domain || !!c.brands?.length) ||
            promptsPerTopic !== initialState.promptsPerTopic ||
            domains.length > 1 ||
            includeSubdomains,
        [
            brandNames,
            detectedBrandNames,
            selectedTopics,
            competitors,
            promptsPerTopic,
            domains.length,
            includeSubdomains,
        ],
    );

    const [shouldGeneratePrompts, setShouldGeneratePrompts] = useState<boolean>(false);
    const {
        data: generatedPromptsData,
        isLoading: isLoadingPrompts,
        cancelQuery: cancelPromptsQuery,
    } = useSetupPrompts(primaryDomain, selectedLanguage.id, selectedTopics, promptsPerTopic, selectedModels, {
        enabled: shouldGeneratePrompts && !!campaignDomainUrls.length && !!selectedTopics?.length,
    });

    const {
        data: generatedCompetitors,
        isLoading: isLoadingCompetitors,
        cancelQuery: cancelCompetitorsQuery,
        error: generatedCompetitorsError,
    } = useGeneratedCompetitors(primaryDomain, brandNames, selectedTopics, selectedCountry.id, selectedLanguage.id, {
        enabled:
            shouldGenerateCompetitors &&
            !!campaignDomainUrls.length &&
            !!brandNames?.length &&
            !!selectedTopics?.length &&
            currentStep === 3,
    });

    const isLoading =
        isInitialDataLoading ||
        areDynamicTopicsLoading ||
        areTrackerConfigurationsLoading ||
        isCreatingCampaign ||
        isCreatingCampaignWithPrompts ||
        (currentStep === 3 && isLoadingCompetitors);
    const isMainButtonDisabled = isLoading || isDetectingBrands || (isLastStep && !campaignDomainUrls.length);

    const clearAndClose = (): void => {
        dispatch({ type: "CLEAR_ALL" });
        setIsOpen(false);
    };

    const onCreationSuccess = async (response: IAiTrackerCreateCampaignResponse): Promise<void> => {
        swRedux.dispatch(
            showSuccessToast(
                translate("llm.setup.modal.success.toast", {
                    trackerName: campaignName,
                }),
            ),
        );
        clearAndClose();
        onSuccessCallback?.(null);

        const newCampaignId = response.campaignId;
        await refetchCampaigns();
        await queryClient.invalidateQueries({ queryKey: campaignKeys.getById(newCampaignId) });
        setCreatedCampaignId(newCampaignId);
    };

    const onCreationError = (error: Error): void => {
        const totalTopics = selectedTopics?.length ?? 0;
        const trackingEventName = `Create AI Tracker/${brandNames.join(",")}, ${campaignDomainUrls.join(",")}/${totalTopics} Topics`;
        allTrackers.dispatchTrackEvent("Button", "error", trackingEventName, {
            topics: totalTopics,
        });
        Sentry.errorTracker({
            e: error,
            message: "Error creating AI Tracker campaign",
            level: "error",
            params: {
                extra: {
                    campaign_name: campaignName,
                    domains: campaignDomainUrls,
                },
            },
        });

        proNavigator.go("digitalsuite_llm_solutions_homepage");
        swRedux.dispatch(
            showErrorToast(
                translate("llm.setup.modal.error.toast", {
                    trackerName: campaignName,
                }),
            ),
        );
    };

    const handleOnConfirm = (): void => {
        if (!campaignDomainUrls?.length) {
            return;
        }

        setShouldGeneratePrompts(false);
        cancelPromptsQuery();
        cancelCompetitorsQuery();

        const totalTopics = selectedTopics?.length ?? 0;
        const trackingEventName = `Create AI Tracker/${brandNames.join(",")}, ${campaignDomainUrls.join(",")}/${totalTopics} Topics`;
        allTrackers.dispatchTrackEvent("Button", "submit-ok", trackingEventName, {
            topics: totalTopics,
            language: (selectedLanguage ?? DEFAULT_LANGUAGE).id,
            country: selectedCountry.id,
            models: JSON.stringify(selectedModels),
        });

        const payload: ICreateCampaignBasePayload = {
            campaignName,
            primaryDomain,
            additionalDomains: campaignDomainUrls.slice(1),
            country: selectedCountry.id,
            brands: brandNames,
            cultureName: selectedLanguage.id,
            includeSubdomains,
            competitorBrands: competitors
                .filter(({ domain, brands }) => domain && brands?.length)
                .map(
                    (competitor) =>
                        ({
                            ...competitor,
                            domain: competitor.domain?.name ?? "",
                        }) as IUpdateCampaignCompetitor,
                ),
        };

        const options = {
            onSuccess: onCreationSuccess,
            onError: onCreationError,
        };

        if (promptsData?.length) {
            createCampaignWithPrompts(
                {
                    ...payload,
                    chatbots: selectedModels,
                    prompts: promptsData
                        .filter(({ topicId, prompts }) => topicId !== ALL_TOPICS_NAME && prompts.length)
                        .flatMap((topic) =>
                            topic.prompts.map(({ query, intent }) => ({
                                query,
                                topicName: topic.name,
                                intent,
                                llms: selectedModels,
                            })),
                        ),
                },
                options,
            );
        } else {
            createCampaign(
                {
                    ...payload,
                    chatbots: selectedModels.map((name) => ({ name })),
                    topics: selectedTopics,
                    promptsPerTopic,
                },
                options,
            );
        }
    };

    const goToStep = (step: number): void => {
        dispatch({
            type: "SET_STEP",
            payload: step,
        });
    };

    const goToNextStep = (): void => goToStep(currentStep + 1);
    const goToPreviousStep = (): void => goToStep(currentStep - 1);

    const {
        isOpen: isPromptsModalOpen,
        modal: promptsModal,
        openModal: openPromptsEditModal,
    } = usePromptsEditModal({
        data: promptsData,
        onSave: (data: IModifiableTopic[]) => {
            setPromptsData(data);
            goToNextStep();
        },
        onManageLaterClick: goToNextStep,
        saveText: translate("common.next"),
        isLoading: isLoadingPrompts,
    });

    useEffect(() => {
        setShouldGeneratePrompts(isPromptsModalOpen);
    }, [isPromptsModalOpen]);

    useEffect(() => {
        if (isPromptsModalOpen) {
            setPromptsData(
                generatedPromptsData?.topics?.map(({ topicName, prompts }) => {
                    const topicId = `setup-topic-[${topicName}]`;
                    const promptsTimestamp = dayjs().toISOString();

                    return {
                        name: topicName,
                        topicId,
                        prompts: prompts.map((prompt, index) => ({
                            ...prompt,
                            promptId: `generated-prompt-[${topicId}:${index}]`,
                            topicId: topicId,
                            timestamp: promptsTimestamp,
                            currentTopicName: topicName,
                        })),
                    };
                }) ?? [],
            );
        }
    }, [generatedPromptsData, isPromptsModalOpen]);

    useEffect(() => {
        if (!isOpen || !domain) {
            return;
        }

        dispatch({
            type: "SET_DOMAINS",
            payload: [domain],
        });
    }, [isOpen, domain]);

    useEffect(() => {
        if (isOpen && fetchedDetectedBrands) {
            dispatch({ type: "APPLY_DETECTED_BRANDS", payload: fetchedDetectedBrands });
        }
    }, [isOpen, fetchedDetectedBrands]);

    useEffect(() => {
        setShowStepErrors(false);
    }, [campaignDomainUrls, isOpen, currentStep, brandNames, competitors, selectedTopics]);

    useEffect(() => {
        if (initialSuggestedTopics !== undefined) {
            const countryDetails = CountryService.getCountryById(initialCountry);
            dispatch({
                type: "INITIALIZE_CAMPAIGN",
                payload: {
                    suggestedTopics: initialSuggestedTopics?.filter((topic) => !topic.includes(",")) ?? [],
                    selectedCountry: countryDetails,
                    selectedLanguage: DEFAULT_LANGUAGE,
                },
            });

            prevLocaleStateRef.current = {
                countryId: countryDetails.id,
                langId: DEFAULT_LANGUAGE.id,
            };

            allTrackers.dispatchTrackEvent(
                "Dropdown",
                "Select",
                `Ai Brand Visibility/Locations/${countryDetails.text}`,
            );
        }
    }, [initialSuggestedTopics, initialCountry]);

    useEffect(() => {
        if (updatedTopicsData) {
            dispatch({
                type: "UPDATE_TOPICS_DATA",
                payload: {
                    //ignore suggestions that are already accepted by the user
                    suggestedTopics: updatedTopicsData.topics.filter(
                        (topic) =>
                            !topic.includes(",") &&
                            !selectedTopics.some(
                                (selectedTopic) => selectedTopic.toLowerCase() === topic.toLowerCase(),
                            ),
                    ),
                },
            });
        }
    }, [updatedTopicsData]);

    //generate competitors
    useEffect(() => {
        if (!generatedCompetitors?.length) {
            return;
        }

        dispatch({ type: "SET_AUTOMATIC_COMPETITORS", payload: generatedCompetitors });
    }, [generatedCompetitors]);

    //navigate to the newly created campaign on success
    useEffect(() => {
        if (createdCampaign) {
            onCampaignCreatedNavigation?.(createdCampaign);
        }
    }, [createdCampaign, onCampaignCreatedNavigation]);

    useEffect(() => {
        prevLocaleStateRef.current = {
            countryId: selectedCountry.id,
            langId: selectedLanguage.id,
        };
    }, [selectedCountry, selectedLanguage]);

    const handleBrandsChange = (brands: string[]): void => {
        dispatch({ type: "SET_BRAND_NAMES", payload: brands.map((value) => value.trim()) });
    };

    const handleDomainsChange = (updatedDomains: IWebsiteItem[]): void => {
        dispatch({ type: "SET_DOMAINS", payload: updatedDomains });
    };

    const handlePromptPerTopicChange = (value: number): void => {
        dispatch({ type: "SET_PROMPTS_PER_TOPIC", payload: value });
    };

    const handleRemoveTopic = (topicToRemove: string): void => {
        allTrackers.dispatchTrackEvent("Button", "click", `Remove/${topicToRemove}`);
        dispatch({ type: "REMOVE_TOPIC", payload: topicToRemove });
    };

    const handleTopicAdded = (newTopic: string): void => {
        allTrackers.dispatchTrackEvent("Button", "click", `Add/${newTopic}`);
        dispatch({ type: "ADD_TOPIC", payload: capitalizeFirstLetterFilter()(newTopic) });

        //remove from suggestions
        dispatch({
            type: "SET_SUGGESTED_TOPICS",
            payload: suggestedTopics.filter((topic) => topic.toLowerCase() !== newTopic.toLowerCase()),
        });
    };

    const handleSuggestionAccepted = (acceptedTopic: string): void => {
        allTrackers.dispatchTrackEvent("Button", "click", `Accept Suggestion/${acceptedTopic}`);
        handleTopicAdded(acceptedTopic);
        dispatch({
            type: "SET_SUGGESTED_TOPICS",
            payload: suggestedTopics.filter((topic) => topic !== acceptedTopic),
        });
    };

    const handleSuggestionReturned = (returnedTopic: string): void => {
        dispatch({
            type: "SET_SUGGESTED_TOPICS",
            payload: [...suggestedTopics, returnedTopic],
        });
    };

    const handleCompetitorChange = (index: number, competitor: IRawCompetitor): void => {
        const updatedCompetitors = [...competitors];
        updatedCompetitors[index] = competitor;

        dispatch({
            type: "SET_MANUAL_COMPETITORS",
            payload: updatedCompetitors,
        });
    };

    const handleCompetitorDelete = (index: number): void => {
        dispatch({
            type: "SET_MANUAL_COMPETITORS",
            payload: competitors.toSpliced(index, 1),
        });
    };

    const handleSetCompetitors = (updated: IRawCompetitor[]): void => {
        dispatch({ type: "SET_MANUAL_COMPETITORS", payload: updated });
    };

    const handleCountriesChange = (countries: ICountryObject): void => {
        dispatch({ type: "SET_SELECTED_COUNTRIES", payload: countries });
    };

    const handleLanguageChange = (language: ILanguage): void => {
        dispatch({ type: "SET_SELECTED_LANGUAGE", payload: language });
    };

    const handleModelsChange = (models: ExplicitLLModel[]): void => {
        dispatch({ type: "SET_SELECTED_MODELS", payload: models });
    };

    const handleSubdomainsChange = (include: boolean): void => {
        dispatch({ type: "SET_INCLUDE_SUBDOMAINS", payload: include });
    };

    const { modal: discardChangesModal, openModal: openDiscardChangesModal } = useDiscardChangesModal(clearAndClose);

    const handleCloseRequest = (): void => {
        if (hasChanges) {
            openDiscardChangesModal();
        } else {
            clearAndClose();
        }
    };

    const stepData = useMemo<IStepData>(
        () => ({
            brandNames,
            domains,
            competitors,
            selectedCountry,
            selectedLanguage,
            suggestedTopics,
            selectedTopics,
            selectedModels,
            promptsPerTopic,
            shouldGenerateCompetitors,
            generatedCompetitorsError,
            includeSubdomains,
            trackerConfigurationsData,
            isLoadingCompetitors,
            isLoading,
            isDetectingBrands,
        }),
        [
            brandNames,
            domains,
            competitors,
            promptsPerTopic,
            selectedCountry,
            selectedLanguage,
            selectedModels,
            selectedTopics,
            suggestedTopics,
            shouldGenerateCompetitors,
            generatedCompetitorsError,
            includeSubdomains,
            trackerConfigurationsData,
            isLoadingCompetitors,
            isLoading,
            isDetectingBrands,
        ],
    );

    const stepErrors = useMemo<IStepError[]>(
        () => currentStepConfig?.validate?.(stepData) ?? [],
        [currentStepConfig, stepData],
    );

    useLayoutEffect(() => {
        if (showStepErrors && !stepErrors.length) {
            setShowStepErrors(false);
        }
    }, [stepErrors, showStepErrors]);

    const stepHandlers: IStepHandlers = {
        handleBrandsChange,
        handleDomainsChange,
        handleCountriesChange,
        handleLanguageChange,
        handleCompetitorChange,
        handleCompetitorDelete,
        handleSetCompetitors,
        handlePromptPerTopicChange,
        handleRemoveTopic,
        handleTopicAdded,
        handleSuggestionAccepted,
        handleSuggestionReturned,
        handleModelsChange,
        handleSubdomainsChange,
        openPromptsEditModal,
    };

    const onMainButtonClick = (): void => {
        if (stepErrors?.length) {
            setShowStepErrors(true);
            return;
        }

        currentStepConfig?.onStepChange?.(stepData, stepHandlers);
        return isLastStep ? handleOnConfirm() : goToNextStep();
    };

    return (
        <BasicModal
            isOpen={isOpen}
            onRequestClose={handleCloseRequest}
            closeIconConfig={{ show: true, onClick: handleCloseRequest }}
            customStyles={{
                content: {
                    width: "532px",
                    height: `${LLM_SETUP_MODAL_HEIGHT}px`,
                    maxHeight: `${LLM_SETUP_MODAL_HEIGHT}px`,
                    padding: 0,
                    overflow: "hidden",
                },
            }}
            shouldCloseOnOverlayClick={false}
            data-automation="llm-setup-modal"
        >
            <FormStepProvider
                currentStep={currentStep}
                currentStepConfig={currentStepConfig}
                goToStep={goToStep}
                goToNext={goToNextStep}
                goToPrevious={goToPreviousStep}
                stepErrors={stepErrors}
                showStepErrors={showStepErrors}
                dismissStepErrors={() => setShowStepErrors(false)}
                stepData={stepData}
                stepHandlers={stepHandlers}
            >
                <Stack
                    height="100%"
                    overflow="hidden"
                >
                    <LLMSetupHeader currentStep={currentStep} />
                    <Flex
                        flex="1 1 auto"
                        overflow="auto"
                    >
                        <LLMSetupForm />
                    </Flex>
                    <LLMSetupFooter
                        mainButtonText={translate(isLastStep ? "llm.setup.modal.save" : "common.next")}
                        secondaryButtonText={translate(canGoBack ? "common.back" : "global.cancel")}
                        onMainButtonClick={onMainButtonClick}
                        onSecondaryButtonClick={canGoBack ? goToPreviousStep : handleCloseRequest}
                        isDisabled={isMainButtonDisabled}
                        isLoading={isLoading}
                    />
                </Stack>
            </FormStepProvider>
            {discardChangesModal}
            {promptsModal}
        </BasicModal>
    );
};
